import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import { getTabsConfiguration } from "Services/BloodLisResultData";
import { getAllPartialRejectedTest } from "Services/BloodPartialRejectedTest";

export interface FilterDataI {
  tabId: number;
  pageSize: number;
  pageNumber: number;
  sortColumn: string;
  sortDirection: string;
  filters: any[];
}

export interface DataI {
  gridHeaders: any[];
  gridColumns: any[];
  gridData: any[];
  printersInfo: any[];
}

type Context = {
  data: DataI;
  total: number;
  value: number;
  open: boolean;
  searchValue: any;
  loading: boolean;
  checkedAll: boolean;
  filterData: FilterDataI;
  intialValue: FilterDataI;
  isMasterExpandTriggered: any;
  apiCalls: () => Promise<void>;
  setIsMasterExpandTriggered: any;
  setData: React.Dispatch<React.SetStateAction<DataI>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  loadGridData: (shouldPageLoad?: boolean) => Promise<any>;
  setSearchValue: React.Dispatch<React.SetStateAction<any>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setCheckedAll: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterData: React.Dispatch<React.SetStateAction<FilterDataI>>;
};

const context = createContext<Context>({} as Context);

export const intialValue: FilterDataI = {
  tabId: 1,
  pageSize: 50,
  pageNumber: 1,
  sortColumn: "RequisitionOrderId",
  sortDirection: "Asc",
  filters: [],
};

export default function DataContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [value, setValue] = useState<number>(0);
  const [isMasterExpandTriggered, setIsMasterExpandTriggered] = useState(false);

  const [filterData, setFilterData] = useState<FilterDataI>(intialValue);
  const [searchValue, setSearchValue] = useState<any>({});
  const [total, setTotal] = useState<number>(0);
  const isFirstLoad = useRef(true);
  const [data, setData] = useState<any>({
    gridHeaders: [],
    gridColumns: [],
    gridData: [],
    printersInfo: [],
  });

  const getTabs = async (): Promise<any> => {
    try {
      const response = await getTabsConfiguration();
      setData((preVal: any) => ({
        ...preVal,
        gridHeaders: response.data.data,
      }));
      const responseTabs = response?.data?.data.sort(
        (a: any, b: any) => a.sortOrder - b.sortOrder
      );
      if (isFirstLoad.current) {
        setValue(responseTabs[0]?.sortOrder - 1);
        isFirstLoad.current = false;
      }
    } catch (error) {
      return error;
    }
  };

  const loadGridData = async (shouldPageLoad = true): Promise<any> => {
    setLoading(shouldPageLoad);

    const cleanedFiltersData = filterData?.filters.map((item: any) => ({
      ...item,
      filterValue:
        typeof item.filterValue === "string"
          ? item.filterValue.trim()
          : item.filterValue,
    }));

    const searchParams = {
      tabId: filterData?.tabId,
      pageNumber: filterData?.pageNumber,
      pageSize: filterData?.pageSize,
      sortColumn: filterData.sortColumn,
      sortDirection: filterData.sortDirection,
      filters: cleanedFiltersData,
      IsLoadExpand: false,
      selectedRow: null,
    };

    try {
      const response = await getAllPartialRejectedTest(searchParams);
      setData((prevVal: any) => ({
        ...prevVal,
        gridData: response.data.data,
      }));
      setTotal(response.data.total);
      return response;
    } catch (error) {
      console.error("Error loading grid data:", error);
      // Clear the grid data on error
      setData((prevVal: any) => ({
        ...prevVal,
        gridData: [],
      }));
      setTotal(0);
      return error;
    } finally {
      setLoading(false);
    }
  };

  const apiCalls = async (): Promise<void> => {
    await getTabs();
    await loadGridData();
  };

  return (
    <context.Provider
      value={{
        data,
        setData,
        filterData,
        total,
        setFilterData,
        searchValue,
        setSearchValue,
        loadGridData,
        loading,
        apiCalls,
        value,
        setValue,
        open,
        setOpen,
        setCheckedAll,
        checkedAll,
        intialValue,
        setLoading,
        isMasterExpandTriggered,
        setIsMasterExpandTriggered,
      }}
    >
      {children}
    </context.Provider>
  );
}

export const useDataContext = () => useContext(context);
